import {
    makeStyles,
    shorthands,
    tokens,
    Button,
    MessageBar,
    MessageBarBody,
    MessageBarTitle,
    Menu,
    MenuTrigger,
    MenuPopover,
    MenuList,
    MenuItem
} from "@fluentui/react-components";
import {
    NavigationRegular,
    DocumentSearchRegular,
    AppsListDetailRegular,
    TaskListLtrRegular,
    SettingsRegular
} from "@fluentui/react-icons";
import { IPagelayoutProps } from "./page-layout.props";
import { PropsWithChildren, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { IMenuItem } from "@interfaces/common.interfaces";
import { getTopLevelMenuItems } from "lib/utils";
import { usePPA } from "context/ppa-context";
import Disabled from "components/initialising-ppa/disabled";
import LicenceExpired from "components/initialising-ppa/licence-expired";
import Breadcrumb from "components/navigation/breadcrumb/breadcrumb";
import { useL10n } from "context/l10n-context";
import { ILocaleStrings } from "loc/locale.interfaces";
import InitialisingError from "components/initialising-ppa/initialising-error";
import Trimmed from "components/common/trimmed/trimmed";
import { PPAPermissions } from "lib/permissions";
import NoPermissions from "components/initialising-ppa/no-permissions";

const layoutStyles = makeStyles({
    mainWrapper: {
        display: 'grid',
        gridTemplateColumns: '1fr',
        gridTemplateRows: 'min-content 1fr',
        height: '100vh',
    },
    header: {
        borderBottomWidth: "1px",
        borderBottomStyle: "solid",
        borderBottomColor: tokens.colorNeutralStrokeSubtle,
        ...shorthands.padding("0em", "1em"),
        display: 'grid',
        gridTemplateColumns: 'min-content 1fr min-content'
    },
    headerDivider: {
        width: "1px",
        height: "75%",
        backgroundColor: tokens.colorNeutralStrokeSubtle,
        ...shorthands.margin(0, "0.5em"),
    },
    menu: {
        display: 'flex',
        alignItems: 'center',
    },
    messageBar: {
        ...shorthands.margin("1em", "2em")
    },
    content: {
        ...shorthands.overflow('auto'),
        position: 'relative'
    }
});

export default function PageLayout(props: PropsWithChildren<IPagelayoutProps>) {
    const styles = layoutStyles();
    const { t } = useL10n<ILocaleStrings>();
    const { hasAccess, hasPermission, isSignupComplete, ssoError, initialisingError, disabled, licenceExpired, permissionLevel } = usePPA();
    const navigate = useNavigate();
    const [hasReportsPermissions, setHasReportsPermissions] = useState<boolean>(false);

    const noPermissions = useMemo<boolean>(() => {
        if (permissionLevel?.length > 0) {
            return !permissionLevel.find(p => p) === true;
        }
        return false;

    }, [permissionLevel]);

    return (
        <main className={styles.mainWrapper}>
            <div>
                {hasAccess && isSignupComplete && !noPermissions &&
                    <div className={styles.header}>
                        <div className={styles.menu}>
                            <Menu>
                                <MenuTrigger>
                                    <Button
                                        appearance="transparent"
                                        icon={<NavigationRegular />} />
                                </MenuTrigger>
                                <MenuPopover>
                                    <MenuList>
                                        <MenuItem
                                            icon={<DocumentSearchRegular />}
                                            onClick={() => navigate('/reports')}>{t("ppaReportsTitle")}</MenuItem>
                                        <Trimmed permission={[PPAPermissions.ViewAllReportRequests, PPAPermissions.ViewMyReportRequests]}>
                                            <MenuItem
                                                icon={<AppsListDetailRegular />}
                                                onClick={() => navigate('/report-requests')}>{t("ppaReportRequestsTitle")}</MenuItem>
                                        </Trimmed>
                                        <Trimmed permission={[PPAPermissions.ViewAllActionRequests, PPAPermissions.ViewMyActionRequests]}>
                                            <MenuItem
                                                icon={<TaskListLtrRegular />}
                                                onClick={() => navigate('/action-requests')}>{t("ppaActionRequestsTitle")}</MenuItem>
                                        </Trimmed>
                                    </MenuList>
                                </MenuPopover>
                            </Menu>
                            <div className={styles.headerDivider}></div>
                        </div>
                        <Breadcrumb />
                        <div>
                            <Trimmed permission={PPAPermissions.ViewSettings}>
                                <Button
                                    appearance="transparent"
                                    icon={<SettingsRegular />}
                                    onClick={() => navigate('/settings')} />
                            </Trimmed>
                        </div>
                    </div>
                }
                {hasAccess && isSignupComplete && noPermissions && <NoPermissions />}
                {ssoError &&
                    <div className={styles.messageBar}>
                        <MessageBar intent="error">
                            <MessageBarBody>
                                <MessageBarTitle>{ssoError}</MessageBarTitle>
                            </MessageBarBody>
                        </MessageBar>
                    </div>}
            </div>
            <div className={styles.content}>
                {initialisingError && <InitialisingError />}
                {!initialisingError && disabled && <Disabled />}
                {!initialisingError && !disabled && licenceExpired && <LicenceExpired />}
                {hasAccess && props.children}
            </div>
        </main>
    );
}